import React from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, Heart, Brain, Users} from 'lucide-react'

export default function HomePage() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#4A90E2] to-[#5DADE2] text-white" aria-labelledby="hero-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-3xl">
            <h1 id="hero-heading" className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Anxiety USA: Your Guide to Understanding and Managing Anxiety
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Evidence-based resources, professional guidance, and support for managing anxiety disorders in America.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/assessment"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl"
                aria-label="Take the GAD-7 anxiety assessment quiz"
              >
                Take the GAD-7 Quiz
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Link>
              <Link
                to="/therapy"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#48C9B0] text-white rounded-lg font-semibold hover:bg-[#3AB89E] transition-all shadow-lg hover:shadow-xl"
                aria-label="Find a therapist near you"
              >
                Find a Therapist
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50" aria-labelledby="stats-heading">
        <h2 id="stats-heading" className="sr-only">Anxiety Statistics in America</h2>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="text-4xl font-bold text-[#4A90E2] mb-2">40M+</div>
              <div className="text-gray-600">Americans with anxiety disorders</div>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-[#4A90E2] mb-2">18%</div>
              <div className="text-gray-600">Of US population affected annually</div>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-[#4A90E2] mb-2">90%+</div>
              <div className="text-gray-600">Improvement with proper treatment</div>
            </div>
          </div>
        </div>
      </section>

      {/* Start Here Section */}
      <section className="py-20" aria-labelledby="start-here-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 id="start-here-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Start Here</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Take the first step toward understanding and managing your anxiety
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <Link
              to="/assessment"
              className="group bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all border border-gray-100 hover:border-[#4A90E2]"
              aria-label="Take an anxiety assessment - Navigate to self-assessment tools"
            >
              <div className="w-12 h-12 bg-[#4A90E2] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-opacity-20 transition-all" aria-hidden="true">
                <Brain className="w-6 h-6 text-[#4A90E2]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Take an Assessment</h3>
              <p className="text-gray-600 mb-4">
                Use the GAD-7 screening tool to understand your anxiety levels and get personalized insights.
              </p>
              <div className="text-[#4A90E2] font-semibold flex items-center">
                Start Quiz <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </div>
            </Link>

            {/* Card 2 */}
            <Link
              to="/therapy"
              className="group bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all border border-gray-100 hover:border-[#48C9B0]"
              aria-label="Explore treatment options - Navigate to therapy guide"
            >
              <div className="w-12 h-12 bg-[#48C9B0] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-opacity-20 transition-all" aria-hidden="true">
                <Heart className="w-6 h-6 text-[#48C9B0]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Explore Treatment Options</h3>
              <p className="text-gray-600 mb-4">
                Learn about Cognitive Behavioral Therapy and evidence-based treatments for anxiety disorders.
              </p>
              <div className="text-[#48C9B0] font-semibold flex items-center">
                Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </div>
            </Link>

            {/* Card 3 */}
            <Link
              to="/conditions"
              className="group bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all border border-gray-100 hover:border-[#5DADE2]"
              aria-label="Understand your condition - Navigate to anxiety conditions directory"
            >
              <div className="w-12 h-12 bg-[#5DADE2] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-opacity-20 transition-all" aria-hidden="true">
                <Users className="w-6 h-6 text-[#5DADE2]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Understand Your Condition</h3>
              <p className="text-gray-600 mb-4">
                Explore different types of anxiety disorders, phobias, and panic conditions with expert insights.
              </p>
              <div className="text-[#5DADE2] font-semibold flex items-center">
                Browse Conditions <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* What is Anxiety Section */}
      <section className="py-20 bg-gray-50" aria-labelledby="understanding-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 id="understanding-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Understanding Anxiety in America
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                Anxiety disorders are the most common mental health condition in the United States, affecting millions of people every year. While everyone experiences worry or fear occasionally, anxiety disorders involve persistent, excessive worry that interferes with daily activities.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                The good news? Anxiety is highly treatable. With proper diagnosis, evidence-based therapy, and support, most people experience significant improvement in their symptoms and quality of life.
              </p>
              <Link
                to="/resources"
                className="inline-flex items-center text-[#4A90E2] font-semibold hover:text-[#48C9B0] transition-colors"
              >
                Read More Resources <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Common Anxiety Disorders</h3>
              <ul className="space-y-4" role="list">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#4A90E2] rounded-full mt-2 mr-3"></div>
                  <div>
                    <div className="font-semibold text-gray-900">Generalized Anxiety Disorder (GAD)</div>
                    <div className="text-gray-600 text-sm">Persistent worry about everyday situations</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#4A90E2] rounded-full mt-2 mr-3"></div>
                  <div>
                    <div className="font-semibold text-gray-900">Panic Disorder</div>
                    <div className="text-gray-600 text-sm">Recurring unexpected panic attacks</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#4A90E2] rounded-full mt-2 mr-3"></div>
                  <div>
                    <div className="font-semibold text-gray-900">Social Anxiety Disorder</div>
                    <div className="text-gray-600 text-sm">Intense fear of social situations</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#4A90E2] rounded-full mt-2 mr-3"></div>
                  <div>
                    <div className="font-semibold text-gray-900">Specific Phobias</div>
                    <div className="text-gray-600 text-sm">Fear of specific objects or situations</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#4A90E2] rounded-full mt-2 mr-3"></div>
                  <div>
                    <div className="font-semibold text-gray-900">Agoraphobia</div>
                    <div className="text-gray-600 text-sm">Fear of places that might cause panic</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#4A90E2] to-[#48C9B0]" aria-labelledby="cta-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="cta-heading" className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Take the First Step?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start with a free self-assessment or explore treatment options that work for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/assessment"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg"
            >
              Take the GAD-7 Quiz
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/therapy"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#4A90E2] transition-all"
            >
              Find a Therapist Near You
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
